import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { audioService } from "@/core/audio/audioService";
import Confetti from "./effects/Confetti";
import Fireworks from "./effects/Fireworks";

const AUTO_RETURN_SECONDS = 12;

/**
 * GameSummaryBasic — cinematic end-of-game recap (Step 9).
 *
 * - Fires a celebration sound + confetti + fireworks on entrance.
 * - Lists the three winners with a staggered reveal.
 * - Displays a 12s countdown ("Returning to Welcome Screen in 12...")
 *   as a visual cue. The actual auto-dismiss is owned by the Host store
 *   (endGame → setTimeout → dismissSummary), so both windows stay in sync.
 * - Host may click "Continue Now" to skip immediately.
 */
export default function GameSummaryBasic({ hotel, winners, calledCount }) {
  const [remaining, setRemaining] = useState(AUTO_RETURN_SECONDS);

  const rows = [
    { key: "one_line", label: "1 LINE" },
    { key: "two_lines", label: "2 LINES" },
    { key: "full_house", label: "FULL HOUSE" },
  ];

  // Play the celebration chord when the summary appears.
  useEffect(() => {
    audioService.playWinner();
  }, []);

  // Visual countdown (source of truth for the auto-dismiss lives on the Host).
  useEffect(() => {
    setRemaining(AUTO_RETURN_SECONDS);
    const id = window.setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div
      data-testid="projector-game-summary"
      className="fixed inset-0 w-screen h-screen flex flex-col items-center justify-center px-16 overflow-hidden"
      style={{ background: "var(--ic-bg-darkest)" }}
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            "radial-gradient(1400px 800px at 50% 40%, var(--ic-primary-glow), transparent 65%)",
        }}
      />

      {/* Celebration layers */}
      <Confetti count={80} />
      <Fireworks burstCount={3} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 flex flex-col items-center text-center"
      >
        <div
          className="text-[10px] tracking-[0.5em] uppercase mb-3"
          style={{ color: "var(--ic-text-muted)" }}
        >
          {hotel?.name || "Your Hotel"} · IC Bingo
        </div>
        <h1
          className="ic-font-display font-bold tracking-tight leading-[0.95]"
          style={{
            fontSize: "clamp(3rem, 7vw, 8rem)",
            color: "var(--ic-text-primary)",
            textShadow: "0 0 40px var(--ic-primary-glow)",
          }}
        >
          Thank you for playing
        </h1>

        <div
          data-testid="projector-summary-called-count"
          className="mt-4 text-2xl tracking-widest"
          style={{ color: "var(--ic-text-secondary)" }}
        >
          {calledCount} numbers called
        </div>

        <div
          className="mt-14 w-[min(900px,80vw)] rounded-2xl border overflow-hidden"
          style={{
            background: "var(--ic-bg-surface)",
            borderColor: "var(--ic-border-subtle)",
          }}
        >
          {rows.map((r, i) => (
            <motion.div
              key={r.key}
              data-testid={`projector-summary-${r.key}`}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 + i * 0.15, duration: 0.5 }}
              className="flex items-center justify-between px-8 py-6"
              style={{
                borderTop:
                  i > 0 ? "1px solid var(--ic-border-subtle)" : "none",
              }}
            >
              <span
                className="ic-font-display font-semibold tracking-[0.3em] uppercase"
                style={{
                  fontSize: "clamp(1rem, 1.4vw, 1.6rem)",
                  color: "var(--ic-text-muted)",
                }}
              >
                {r.label}
              </span>
              <span
                className="ic-font-display"
                style={{
                  fontSize: "clamp(1.4rem, 2vw, 2.4rem)",
                  color: winners?.[r.key]?.name
                    ? "var(--ic-primary)"
                    : "var(--ic-text-muted)",
                }}
              >
                {winners?.[r.key]?.name || "—"}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Countdown badge */}
        <motion.div
          data-testid="projector-summary-countdown"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.6 }}
          className="mt-14 inline-flex items-center gap-3 px-6 py-3 rounded-full border"
          style={{
            borderColor: "var(--ic-border-subtle)",
            background: "rgba(0,0,0,0.35)",
            backdropFilter: "blur(8px)",
          }}
        >
          <Sparkles
            className="w-4 h-4"
            style={{ color: "var(--ic-primary)" }}
          />
          <span
            className="text-xs tracking-[0.4em] uppercase"
            style={{ color: "var(--ic-text-secondary)" }}
          >
            Returning to Welcome Screen in
          </span>
          <span
            data-testid="projector-summary-countdown-value"
            className="ic-font-display font-bold tabular-nums"
            style={{
              fontSize: "1.8rem",
              color: "var(--ic-primary)",
              minWidth: "2ch",
              textAlign: "center",
              textShadow: "0 0 20px var(--ic-primary-glow)",
            }}
          >
            {remaining}
          </span>
        </motion.div>
      </motion.div>
    </div>
  );
}
